import { useCallback, useRef, useState } from 'react';

export interface KeysetPagination {
  /** Zero-based current page index. */
  pageNumber: number;
  /**
   * Cursor to pass as the query's `after` for the current page
   * (null = first page).
   */
  after: string | null;
  /**
   * True while a page change is fetching. Fold this into the table's
   * `isLoading` so navigation shows a full loader instead of stale rows under
   * the new page number. (Search/filter refetches should use the subtle
   * `isLoadingCache` bar instead — this stays false for those.)
   */
  isPaginating: boolean;
  /** Handle a page change; pass the new zero-based page index. */
  onPageChange: (newPage: number) => void;
  /**
   * Call once per settled fetch (from a `useEffect` keyed on the query's
   * `data`), passing the current page's server `endCursor`. Records where the
   * next page starts and clears the paginating loader.
   */
  onPageLoaded: (endCursor?: string | null) => void;
  /**
   * Reset to page 0 and clear stored cursors. Call synchronously in every
   * filter / search / sort / page-size change handler (alongside the state
   * update) so the next fetch starts from the first page.
   */
  reset: () => void;
}

/**
 * Server-side keyset (cursor) pagination for list tables backed by a Relay
 * connection hook. Stores one real server `endCursor` per page boundary and
 * fetches only the current page — no client-side accumulation. This is the
 * shared implementation behind the hardware list pages; use it for every
 * server-paginated `PaginationTable`.
 *
 * Only adjacent pages are reachable via the table's prev/next controls. Each
 * page boundary's `endCursor` is captured when that page's data settles (via
 * `onPageLoaded`, not at click time), so navigation — including rapid prev/next
 * before a fetch finishes — can never store a stale cursor.
 *
 * The query owns `data`/`endCursor` but needs `after` as input, so the page
 * feeds the settled cursor back through `onPageLoaded` in a small effect:
 *
 * @example
 * const { pageNumber, after, isPaginating, onPageChange, onPageLoaded, reset } =
 *   useKeysetPagination();
 *
 * const { data, count, hasMore, endCursor, isLoading, isRefetching } =
 *   useThingRelayQuery(filter, { limit: pageSize, after, sort });
 *
 * useEffect(() => onPageLoaded(endCursor), [data]); // eslint-disable-line
 *
 * // In each filter/search/sort/pageSize handler: call reset() next to setState.
 *
 * <PaginationTable
 *   page={pageNumber}
 *   onPageChange={(_, p) => onPageChange(p)}
 *   isLoading={initialLoading || isPaginating}
 *   isLoadingCache={isRefetching}
 *   totalItems={count}
 *   hasMore={hasMore}
 *   noDataSlice
 *   localSearch={false}
 * />
 */
export function useKeysetPagination(): KeysetPagination {
  const [pageNumber, setPageNumber] = useState(0);
  const [pageCursors, setPageCursors] = useState<Record<number, string | null>>({
    0: null,
  });
  const [isPaginating, setIsPaginating] = useState(false);

  // Mirror pageNumber in a ref so onPageLoaded stays referentially stable
  // (so the page's effect can depend only on `data`) while still reading the
  // page the settled cursor belongs to.
  const pageNumberRef = useRef(0);
  pageNumberRef.current = pageNumber;

  const onPageLoaded = useCallback((endCursor?: string | null) => {
    const currentPage = pageNumberRef.current;
    if (endCursor) {
      setPageCursors((prev) =>
        prev[currentPage + 1] === endCursor
          ? prev
          : { ...prev, [currentPage + 1]: endCursor }
      );
    }
    setIsPaginating(false);
  }, []);

  const onPageChange = useCallback((newPage: number) => {
    setIsPaginating(true);
    setPageNumber(newPage);
  }, []);

  const reset = useCallback(() => {
    setPageNumber(0);
    setPageCursors({ 0: null });
    setIsPaginating(false);
  }, []);

  return {
    pageNumber,
    after: pageCursors[pageNumber] ?? null,
    isPaginating,
    onPageChange,
    onPageLoaded,
    reset,
  };
}

export default useKeysetPagination;
